if (!customElements.get("configurator-steps")) {
    // Step rail for the configurator drawer: a vertical list on desktop, a scrollable
    // bar pinned to the top of the panel on mobile. Built from the steps already in the
    // DOM, so the Liquid step logic stays the single source of truth.
    class ConfiguratorSteps extends HTMLElement {
        connectedCallback() {
            // Najpierw WLASNY panel (szyna stoi obok swojego obszaru przewijania),
            // dopiero potem cala szuflada. `closest(".drawer")` potrafi trafic w
            // wspolne opakowanie szuflad, a wtedy `querySelector` zwracal pierwszy
            // `.drawer__scroll` w dokumencie - z innej szuflady, bez krokow. Szyna
            // liczyla wtedy zero krokow, chowala sie, a razem z nia znikala kolumna
            // siatki i tresc wpadala w 141 px (zgloszenie Patryka 4 wrz 2026).
            const ownScroller = this.parentElement
                ? this.parentElement.querySelector(".drawer__scroll")
                : null;
            const drawer = this.closest(".drawer");

            this.scroller = ownScroller || (drawer ? drawer.querySelector(".drawer__scroll") : null);
            this.template = this.getAttribute("data-step-label") || "%s";

            if (!this.scroller) return;

            this.render();

            // Steps are re-rendered by the configurator when options change.
            this.watcher = new MutationObserver(() => this.render());
            this.watcher.observe(this.scroller, { childList: true, subtree: true });
        }

        disconnectedCallback() {
            if (this.watcher) this.watcher.disconnect();
            if (this._raf) cancelAnimationFrame(this._raf);
        }

        get steps() {
            // Sections, not options: the dimension options share one numbered section,
            // so the rail has to follow the headings the user actually sees.
            return Array.prototype.slice.call(
                this.scroller.querySelectorAll(".product-configurator__section")
            );
        }

        render() {
            const steps = this.steps;

            // Porownanie po elementach, nie po liczbie: zmiana stelaza laduje inny
            // produkt i podmienia sekcje przez innerHTML, wiec liczba sie zgadza,
            // ale obserwowane wezly sa juz odlaczone i podswietlenie zamiera.
            if (
                this._steps &&
                steps.length === this._steps.length &&
                steps.every((s, i) => s === this._steps[i])
            ) return;
            this._steps = steps;

            this.innerHTML = "";

            if (steps.length < 2) {
                this.hidden = true;
                return;
            }

            this.hidden = false;
            this.items = steps.map((step, index) => {
                const item = document.createElement("button");

                item.type = "button";
                item.className = "product-configurator__steps-item";
                item.textContent = this.template.replace("%s", index + 1);
                item.addEventListener("click", () => this.goTo(step));
                this.appendChild(item);

                return item;
            });

            this.watch(steps);
        }

        goTo(step) {
            // offsetTop is relative to the nearest positioned ancestor, which is not the
            // scroller here, so the offset is measured from the rendered boxes instead.
            const target =
                this.scroller.scrollTop +
                step.getBoundingClientRect().top -
                this.scroller.getBoundingClientRect().top;

            this.scrollTo(target);
        }

        // The panel is driven by perfect-scrollbar and has overflow hidden, so the native
        // smooth behaviour is ignored and the animation is done by hand.
        scrollTo(target) {
            const start = this.scroller.scrollTop;
            const distance = target - start;
            const duration = 320;
            let startedAt = null;

            const step = (timestamp) => {
                if (startedAt === null) startedAt = timestamp;

                const progress = Math.min((timestamp - startedAt) / duration, 1);
                const eased = progress < 0.5
                    ? 2 * progress * progress
                    : 1 - Math.pow(-2 * progress + 2, 2) / 2;

                this.scroller.scrollTop = start + distance * eased;

                if (progress < 1) requestAnimationFrame(step);
            };

            requestAnimationFrame(step);
        }

        watch() {
            // Scroll-spy liczony z zywego DOM przy kazdym przewinieciu: aktywny jest
            // OSTATNI krok, ktorego gorna krawedz minela linie 30% wysokosci kadru.
            // IntersectionObserver na zapamietanych wezlach gubil sie, gdy dwa kroki
            // naraz przecinaly pas detekcji (dlugi krok tkanin obok krotkiego) i po
            // kazdej podmianie DOM.
            if (!this._scrollBound) {
                this._scrollBound = true;
                this.scroller.addEventListener(
                    "scroll",
                    () => {
                        if (this._raf) return;
                        this._raf = requestAnimationFrame(() => {
                            this._raf = null;
                            this.recalc();
                        });
                    },
                    { passive: true }
                );

                // Pierwsze przeliczenie robimy, gdy panel dostaje wymiary
                // (otwarcie szuflady) - w zamknietej wszystko ma zero i recalc
                // nie ma na czym pracowac. ResizeObserver zamiast zdarzenia
                // drawerOpen, bo glowna szuflada otwiera sie bez tego zdarzenia.
                this._sizeWatch = new ResizeObserver(() => this.recalc());
                this._sizeWatch.observe(this.scroller);

                // Glowna szuflada nie zmienia rozmiaru przy otwieraniu (chowa
                // ja transform), wiec ResizeObserver milczy - przeliczamy tez
                // na zdarzeniu otwarcia.
                this._drawer = this.closest("drawer-section");
                window.addEventListener("drawerOpen", (e) => {
                    if (this._drawer && e.detail && e.detail.id === this._drawer.id) {
                        requestAnimationFrame(() => this.recalc());
                    }
                });
            }

            this.recalc();
        }

        recalc() {
            const steps = this.steps;
            if (!steps.length) return;

            const rootRect = this.scroller.getBoundingClientRect();

            // Zamknieta szuflada ma zerowe wymiary - kazdy krok "spelnia"
            // wtedy warunek i podswietlal sie OSTATNI, dopoki uzytkownik nie
            // ruszyl scrollem (zgloszenie Patryka 2026-09-03). Liczymy dopiero
            // na otwartym panelu; otwarcie przelicza przez zdarzenie drawerOpen.
            if (rootRect.height < 10) return;

            // Panel nieprzewiniety = zawsze krok pierwszy. To ucina wszystkie
            // stany przejsciowe otwierania szuflady (animacja transform,
            // visibility), w ktorych geometria potrafi wskazac ostatni krok.
            if (this.scroller.scrollTop < 2) {
                this.setActive(0);
                return;
            }

            const line = rootRect.top + rootRect.height * 0.3;
            let index = 0;

            steps.forEach((step, i) => {
                if (step.getBoundingClientRect().top <= line) index = i;
            });

            this.setActive(index);
        }

        setActive(index) {
            if (!this.items || index < 0) return;

            this.items.forEach((item, i) => item.classList.toggle("selected", i === index));
            this.setProgress(index + 1, this.items.length);

            const active = this.items[index];

            if (active && this.scrollWidth > this.clientWidth) {
                active.scrollIntoView({ block: "nearest", inline: "center" });
            }
        }
    }

    ConfiguratorSteps.prototype.setProgress = function (current, total) {
        const ring = document.querySelector("[data-configurator-progress]");

        if (!ring || !total) return;

        ring.style.setProperty("--progress", Math.round((current / total) * 100));
        ring.setAttribute("aria-valuenow", current);
        ring.setAttribute("aria-valuemax", total);
    };

    customElements.define("configurator-steps", ConfiguratorSteps);
}
